import { useEffect, useState } from 'react'
import { api, ErroApi } from './api.js'
import { paraApi, type ItemCarrinho } from './carrinho.js'

export type Cotacao = {
  subtotalCents: number
  taxaEntregaCents: number
  descontoCents: number
  totalCents: number
  tempoEstimadoMin: number
  origemTaxa: string
  itens: {
    produtoId: string
    nome: string
    quantidade: number
    precoUnitCents: number
    adicionaisCents: number
    totalCents: number
  }[]
}

/**
 * Pede o total ao servidor.
 *
 * Toda vez que o carrinho ou a entrega mudam. Nunca somamos preço no
 * navegador: se o cálculo vivesse aqui, o valor da tela e o do pedido
 * poderiam divergir — e divergência de centavo no checkout é cliente perdido.
 */
export function useCotacao(
  itens: ItemCarrinho[],
  entrega: {
    tipo: 'ENTREGA' | 'RETIRADA'
    bairroId?: string | undefined
    /**
     * Endereço REAL, quando já preenchido.
     *
     * Antes daqui ia `rua: 'a definir', numero: '0'` — um endereço de
     * mentira, que o geocoding nunca acha. Resultado: a cotação caía sempre
     * na taxa padrão enquanto o pedido de verdade calculava por raio, e o
     * cliente via um preço e pagava outro. Com o endereço certo, os dois
     * caminhos fazem a mesma conta.
     */
    endereco?: { rua: string; numero: string; bairroTexto?: string | undefined } | undefined
  },
) {
  const [cotacao, setCotacao] = useState<Cotacao | null>(null)
  const [erro, setErro] = useState<ErroApi | null>(null)
  const [carregando, setCarregando] = useState(false)

  const chave = JSON.stringify([paraApi(itens), entrega])

  useEffect(() => {
    if (itens.length === 0) {
      setCotacao(null)
      setErro(null)
      return
    }
    const ctrl = new AbortController()
    setCarregando(true)
    void (async () => {
      try {
        const r = await api<Cotacao>('/v1/publico/cotacao', {
          method: 'POST',
          signal: ctrl.signal,
          corpo: {
            itens: paraApi(itens),
            tipo: entrega.tipo,
            ...(entrega.tipo === 'ENTREGA'
              ? {
                  endereco: {
                    // Sem endereço preenchido ainda, manda um marcador: a
                    // cotação precisa de algo para calcular o subtotal, e o
                    // servidor cai na taxa padrão até a rua existir.
                    rua: entrega.endereco?.rua || 'a definir',
                    numero: entrega.endereco?.numero || '0',
                    ...(entrega.endereco?.bairroTexto
                      ? { bairroTexto: entrega.endereco.bairroTexto }
                      : {}),
                    ...(entrega.bairroId ? { bairroId: entrega.bairroId } : {}),
                  },
                }
              : {}),
          },
        })
        setCotacao(r)
        setErro(null)
      } catch (e) {
        if (ctrl.signal.aborted) return
        setErro(e instanceof ErroApi ? e : new ErroApi(0, 'REDE', 'Sem conexão com a loja.'))
        setCotacao(null)
      } finally {
        if (!ctrl.signal.aborted) setCarregando(false)
      }
    })()
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chave])

  return { cotacao, erro, carregando }
}
